import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Eye, EyeOff, ArrowRight, Check, AlertCircle, ArrowLeft, Mail, Loader2 } from 'lucide-react';

type Props = {
  onSuccess: () => void;
  pendingEmail?: string | null;
  pendingAgencyName?: string | null;
  pendingPassword?: string | null;
  onGoCheckout: () => void;
  onBack?: () => void;
};

type Mode = 'signin' | 'signup' | 'reset' | 'verify';

export default function AuthPage({ onSuccess, pendingEmail, pendingAgencyName, pendingPassword, onBack }: Props) {
  const [mode, setMode] = useState<Mode>(pendingEmail ? 'signup' : 'signin');
  const [email, setEmail] = useState(pendingEmail ?? '');
  const [password, setPassword] = useState(pendingPassword ?? '');
  const [confirmPassword, setConfirmPassword] = useState(pendingPassword ?? '');
  const [agencyName, setAgencyName] = useState(pendingAgencyName ?? '');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [autoCreating, setAutoCreating] = useState(false);
  const autoCreateRan = useRef(false);

  // Auto-create account after returning from successful payment
  useEffect(() => {
    if (autoCreateRan.current) return;
    if (pendingEmail && pendingPassword && pendingAgencyName && mode === 'signup') {
      autoCreateRan.current = true;
      setAutoCreating(true);
      setAcceptTerms(true);
      setAcceptPrivacy(true);
      (async () => {
        try {
          const { data, error: signUpErr } = await supabase.auth.signUp({
            email: pendingEmail,
            password: pendingPassword,
            options: { data: { role: 'agency' } },
          });
          if (signUpErr) throw signUpErr;
          if (!data.user) throw new Error('Sign up failed — no user returned.');

          const baseSlug = slugify(pendingAgencyName) || 'agency';
          const uniqueSlug = `${baseSlug}-${Math.random().toString(36).slice(2, 6)}`;

          const { error: agencyErr } = await supabase.from('agencies').insert({
            user_id: data.user.id,
            name: pendingAgencyName,
            slug: uniqueSlug,
            email: pendingEmail,
            subscription_status: 'active',
          });
          if (agencyErr) throw agencyErr;

          if (!data.session) {
            setMode('verify');
            setInfo('Payment received! Check your email for a verification link to activate your account.');
          } else {
            onSuccess();
          }
        } catch (err: unknown) {
          setError(err instanceof Error ? err.message : 'Something went wrong creating your account.');
        } finally {
          setAutoCreating(false);
        }
      })();
    }
  }, [pendingEmail, pendingPassword, pendingAgencyName, mode, onSuccess]);

  const pwScore = (() => {
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();
  const pwLabel = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'][pwScore];
  const pwColor = ['bg-ink-400', 'bg-ink-400', 'bg-ink-500', 'bg-ink-700', 'bg-ink-900'][pwScore];

  function slugify(name: string) {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setInfo('');

    if (mode === 'reset') {
      setLoading(true);
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      setLoading(false);
      if (resetErr) {
        setError(resetErr.message);
      } else {
        setResetSent(true);
      }
      return;
    }

    if (mode === 'verify') {
      setLoading(true);
      const { error: verifyErr } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      setLoading(false);
      if (verifyErr) {
        setError(verifyErr.message);
      } else {
        setInfo('Verification email sent. Check your inbox and click the link to activate your account.');
      }
      return;
    }

    setLoading(true);

    try {
      if (mode === 'signup') {
        if (!agencyName.trim()) { setError('Agency name is required.'); setLoading(false); return; }
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(agencyName.trim())) { setError("Agency name can't be an email address — please enter your company name."); setLoading(false); return; }
        if (agencyName.trim().length < 2) { setError('Agency name must be at least 2 characters.'); setLoading(false); return; }
        if (!acceptTerms) { setError('Please accept the Terms of Service to continue.'); setLoading(false); return; }
        if (!acceptPrivacy) { setError('Please accept the Privacy Policy to continue.'); setLoading(false); return; }
        if (password.length < 8) { setError('Password must be at least 8 characters.'); setLoading(false); return; }
        if (password !== confirmPassword) { setError('Passwords do not match.'); setLoading(false); return; }

        const { data, error: signUpErr } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { role: 'agency' } },
        });
        if (signUpErr) throw signUpErr;
        if (!data.user) throw new Error('Sign up failed — no user returned.');

        const baseSlug = slugify(agencyName) || 'agency';
        const uniqueSlug = `${baseSlug}-${Math.random().toString(36).slice(2, 6)}`;

        const { error: agencyErr } = await supabase.from('agencies').insert({
          user_id: data.user.id,
          name: agencyName.trim(),
          slug: uniqueSlug,
          email,
          subscription_status: 'active',
        });
        if (agencyErr) throw agencyErr;

        if (!data.session) {
          setMode('verify');
          setInfo('Account created! Check your email for a verification link to activate your account.');
        } else {
          onSuccess();
        }
      } else {
        const { data, error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
        if (signInErr) throw signInErr;
        if (!data.session) {
          setMode('verify');
          setInfo('Please verify your email before signing in. Check your inbox for the verification link.');
        } else {
          onSuccess();
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-ink-950 flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-md">
        {onBack && mode !== 'verify' && (
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-ink-400 hover:text-white text-sm font-medium mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        )}

        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <h1 className="text-2xl font-bold text-white tracking-tight">CareHire</h1>
          </div>
          <p className="text-ink-500 text-sm">
            {mode === 'reset' ? 'Reset your password' : mode === 'verify' ? 'Verify your email' : 'Smart recruitment for care agencies'}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-lg shadow-2xl p-8">
          {pendingEmail && mode === 'signup' && !autoCreating && (
            <div className="flex items-start gap-2.5 p-3 mb-5 rounded-md bg-green-50 border border-green-100">
              <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">
                Payment successful! Confirm your details below to activate your account.
              </p>
            </div>
          )}

          {autoCreating && (
            <div className="flex items-center justify-center gap-2.5 py-12">
              <Loader2 className="w-6 h-6 animate-spin text-ink-900" />
              <span className="text-sm font-medium text-ink-700">Creating your account...</span>
            </div>
          )}

          {!autoCreating && mode === 'verify' ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 bg-ink-50 rounded-md flex items-center justify-center mx-auto mb-5">
                <Mail className="w-6 h-6 text-ink-900" />
              </div>
              <h2 className="text-xl font-bold text-ink-900 mb-2">Check your email</h2>
              <p className="text-sm text-ink-500 mb-6">
                We sent a verification link to <strong className="text-ink-700">{email}</strong>.
                Click the link to activate your account, then sign in.
              </p>
              {info && (
                <div className="flex items-start gap-2.5 p-3 mb-4 rounded-md bg-ink-50 border border-ink-100">
                  <Check className="w-4 h-4 text-ink-900 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-ink-700">{info}</p>
                </div>
              )}
              {error && (
                <div className="flex items-start gap-2.5 p-3 mb-4 rounded-md bg-red-50 border border-red-100">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              <div className="space-y-3">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full py-3 bg-ink-900 hover:bg-ink-800 disabled:opacity-60 text-white font-semibold rounded-md transition-colors text-sm"
                >
                  {loading ? 'Sending...' : 'Resend verification email'}
                </button>
                <button
                  onClick={() => setMode('signin')}
                  className="w-full py-3 bg-ink-50 hover:bg-ink-100 text-ink-700 font-semibold rounded-md transition-colors text-sm"
                >
                  Back to sign in
                </button>
              </div>
            </div>
          ) : !autoCreating && mode === 'reset' ? (
            <>
              {resetSent ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 bg-ink-50 rounded-md flex items-center justify-center mx-auto mb-5">
                    <Check className="w-6 h-6 text-ink-900" />
                  </div>
                  <h2 className="text-xl font-bold text-ink-900 mb-2">Check your email</h2>
                  <p className="text-sm text-ink-500 mb-6">
                    We sent a password reset link to <strong className="text-ink-700">{email}</strong>.
                  </p>
                  <button
                    onClick={() => { setMode('signin'); setResetSent(false); }}
                    className="w-full py-3 bg-ink-900 hover:bg-ink-800 text-white font-semibold rounded-md transition-colors text-sm"
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-ink-500 mb-5">
                    Enter your email and we'll send you a link to reset your password.
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1.5">Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@agency.com"
                        required
                        className="w-full px-4 py-3 rounded-md border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-ink-900 focus:border-transparent"
                      />
                    </div>
                    {error && (
                      <div className="flex items-start gap-2.5 p-3 rounded-md bg-red-50 border border-red-100">
                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-ink-900 hover:bg-ink-800 disabled:opacity-60 text-white font-semibold rounded-md transition-colors text-sm"
                    >
                      {loading ? 'Sending...' : 'Send reset link'}
                      {!loading && <ArrowRight className="w-4 h-4" />}
                    </button>
                  </form>
                  <button
                    onClick={() => setMode('signin')}
                    className="w-full text-center text-sm text-ink-500 hover:text-ink-900 mt-4"
                  >
                    Back to sign in
                  </button>
                </>
              )}
            </>
          ) : !autoCreating ? (
            <>
              {/* Tabs */}
              <div className="flex rounded-md bg-ink-50 p-1 mb-6">
                {(['signin', 'signup'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => { setMode(m); setError(''); setInfo(''); }}
                    className={`flex-1 py-2 text-sm font-semibold rounded transition-colors ${
                      mode === m ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-700'
                    }`}
                  >
                    {m === 'signin' ? 'Sign In' : 'Create Account'}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'signup' && (
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1.5">Agency Name</label>
                    <input
                      type="text"
                      value={agencyName}
                      onChange={(e) => setAgencyName(e.target.value)}
                      placeholder="e.g. Sunrise Care Agency"
                      required
                      className="w-full px-4 py-3 rounded-md border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-ink-900 focus:border-transparent"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    required
                    className="w-full px-4 py-3 rounded-md border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-ink-900 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={mode === 'signup' ? 8 : 1}
                      className="w-full px-4 py-3 pr-11 rounded-md border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-ink-900 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600"
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {mode === 'signup' && password.length > 0 && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[0, 1, 2, 3].map((i) => (
                          <div key={i} className={`h-1 flex-1 rounded-full ${i < pwScore ? pwColor : 'bg-ink-100'}`} />
                        ))}
                      </div>
                      <p className="text-xs text-ink-400">{pwLabel} — use 8+ characters with a mix of letters, numbers, and symbols</p>
                    </div>
                  )}
                </div>

                {mode === 'signup' && (
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1.5">Confirm Password</label>
                    <div className="relative">
                      <input
                        type={showPw ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        minLength={8}
                        className="w-full px-4 py-3 pr-11 rounded-md border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-ink-900 focus:border-transparent"
                      />
                      {confirmPassword.length > 0 && password !== confirmPassword && (
                        <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                      )}
                    </div>
                  </div>
                )}

                {mode === 'signup' && (
                  <>
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-ink-300 text-ink-900 focus:ring-ink-900"
                      />
                      <span className="text-xs text-ink-500 leading-relaxed">
                        I agree to the <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'terms' }))} className="text-ink-900 hover:underline">Terms of Service</button> and <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'acceptable-use' }))} className="text-ink-900 hover:underline">Acceptable Use Policy</button>.
                      </span>
                    </label>
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptPrivacy}
                        onChange={(e) => setAcceptPrivacy(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-ink-300 text-ink-900 focus:ring-ink-900"
                      />
                      <span className="text-xs text-ink-500 leading-relaxed">
                        I agree to the <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'privacy' }))} className="text-ink-900 hover:underline">Privacy Policy</button> and <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'cookies' }))} className="text-ink-900 hover:underline">Cookie Policy</button>.
                      </span>
                    </label>
                  </>
                )}

                {mode === 'signin' && (
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => { setMode('reset'); setError(''); }}
                      className="text-xs text-ink-500 hover:text-ink-900 font-medium"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                {info && (
                  <div className="flex items-start gap-2.5 p-3 rounded-md bg-ink-50 border border-ink-100">
                    <Check className="w-4 h-4 text-ink-900 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-ink-700">{info}</p>
                  </div>
                )}

                {error && (
                  <div className="flex items-start gap-2.5 p-3 rounded-md bg-red-50 border border-red-100">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-ink-900 hover:bg-ink-800 disabled:opacity-60 text-white font-semibold rounded-md transition-colors text-sm"
                >
                  {loading ? 'Please wait...' : mode === 'signup' ? 'Create account' : 'Sign in'}
                  {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
              </form>

              {mode === 'signin' && (
                <p className="text-center text-sm text-ink-400 mt-5">
                  Don't have an account yet?{' '}
                  <button onClick={() => setMode('signup')} className="text-ink-900 font-medium hover:underline">
                    Create one
                  </button>
                </p>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
